import React from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsService, commentsService, upvotesService } from "@/services";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Trophy, Award, Star, MapPin, TrendingUp, Users } from "lucide-react";
import { motion } from "framer-motion";

export default function Leaderboard() {
  const { data: reports = [] } = useQuery({
    queryKey: ['allReportsLeaderboard'],
    queryFn: () => reportsService.list(),
    initialData: [],
  });

  const { data: comments = [] } = useQuery({
    queryKey: ['allComments'],
    queryFn: () => commentsService.list(),
    initialData: [],
  });

  const { data: upvotes = [] } = useQuery({
    queryKey: ['allUpvotes'],
    queryFn: () => upvotesService.list(),
    initialData: [],
  });

  // Calculate top contributors
  const userStats = {};
  
  reports.forEach(report => {
    const email = report.created_by_email;
    if (!email) return; // Skip if no email
    
    if (!userStats[email]) {
      userStats[email] = { 
        email: email, 
        reports: 0, 
        comments: 0, 
        upvotes: 0,
        resolved: 0 
      };
    }
    userStats[email].reports++;
    if (report.status === 'resolved') {
      userStats[email].resolved++;
    }
  });

  comments.forEach(comment => {
    if (!userStats[comment.user_email]) {
      userStats[comment.user_email] = { 
        email: comment.user_email, 
        reports: 0, 
        comments: 0, 
        upvotes: 0,
        resolved: 0 
      };
    }
    userStats[comment.user_email].comments++;
  });

  upvotes.forEach(upvote => {
    if (!userStats[upvote.user_email]) {
      userStats[upvote.user_email] = { 
        email: upvote.user_email, 
        reports: 0, 
        comments: 0, 
        upvotes: 0,
        resolved: 0 
      };
    }
    userStats[upvote.user_email].upvotes++;
  });

  const topContributors = Object.values(userStats)
    .map(user => ({
      ...user,
      score: user.reports * 10 + user.comments * 2 + user.upvotes * 1
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  // Calculate top neighborhoods
  const neighborhoodStats = {};
  reports.forEach(report => {
    const neighborhood = report.neighborhood || 'Unknown';
    if (!neighborhoodStats[neighborhood]) {
      neighborhoodStats[neighborhood] = { 
        name: neighborhood, 
        total: 0, 
        resolved: 0,
        active: 0 
      };
    }
    neighborhoodStats[neighborhood].total++;
    if (report.status === 'resolved') {
      neighborhoodStats[neighborhood].resolved++;
    }
    if (report.status === 'in_progress' || report.status === 'acknowledged') {
      neighborhoodStats[neighborhood].active++;
    }
  });

  const topNeighborhoods = Object.values(neighborhoodStats)
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  // Most active categories
  const categoryStats = {};
  reports.forEach(report => {
    const category = report.category;
    categoryStats[category] = (categoryStats[category] || 0) + 1;
  });

  const topCategories = Object.entries(categoryStats)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const getMedalColor = (index) => {
    if (index === 0) return "text-yellow-500";
    if (index === 1) return "text-gray-400";
    if (index === 2) return "text-amber-600";
    return "text-gray-300";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Trophy className="w-10 h-10 text-yellow-500" />
            <h1 className="text-4xl font-bold text-gray-900">Community Leaderboard</h1>
          </div>
          <p className="text-gray-600 text-lg">
            Celebrating our top contributors making Cape Town better
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Top 3 Podium */}
          <div className="lg:col-span-2">
            <Card className="p-8 bg-white/90 backdrop-blur-sm">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Award className="w-6 h-6 text-emerald-600" />
                Top Contributors
              </h2>
              
              {/* Podium */}
              {topContributors.length >= 3 && (
                <div className="flex items-end justify-center gap-4 mb-8">
                  {/* 2nd Place */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-col items-center"
                  >
                    <Avatar className="w-16 h-16 mb-3 border-4 border-gray-400">
                      <AvatarFallback className="bg-gray-200 text-gray-700 text-lg">
                        {topContributors[1]?.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Trophy className="w-8 h-8 text-gray-400 mb-2" />
                    <div className="bg-gradient-to-t from-gray-400 to-gray-300 rounded-t-lg p-4 w-32 text-center">
                      <p className="text-white font-bold text-sm truncate">
                        {topContributors[1]?.email.split('@')[0]}
                      </p>
                      <p className="text-white text-2xl font-bold">{topContributors[1]?.score}</p>
                      <p className="text-white text-xs">points</p>
                    </div>
                    <div className="bg-gray-500 h-20 w-32 flex items-center justify-center text-white font-bold text-2xl">
                      2
                    </div>
                  </motion.div>

                  {/* 1st Place */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-col items-center"
                  >
                    <Avatar className="w-20 h-20 mb-3 border-4 border-yellow-400">
                      <AvatarFallback className="bg-yellow-200 text-yellow-700 text-xl">
                        {topContributors[0]?.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Trophy className="w-10 h-10 text-yellow-500 mb-2" />
                    <div className="bg-gradient-to-t from-yellow-500 to-yellow-400 rounded-t-lg p-4 w-32 text-center">
                      <p className="text-white font-bold text-sm truncate">
                        {topContributors[0]?.email.split('@')[0]}
                      </p>
                      <p className="text-white text-3xl font-bold">{topContributors[0]?.score}</p>
                      <p className="text-white text-xs">points</p>
                    </div>
                    <div className="bg-yellow-600 h-28 w-32 flex items-center justify-center text-white font-bold text-3xl">
                      1
                    </div>
                  </motion.div>

                  {/* 3rd Place */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col items-center"
                  >
                    <Avatar className="w-16 h-16 mb-3 border-4 border-amber-600">
                      <AvatarFallback className="bg-amber-200 text-amber-700 text-lg">
                        {topContributors[2]?.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Trophy className="w-8 h-8 text-amber-600 mb-2" />
                    <div className="bg-gradient-to-t from-amber-600 to-amber-500 rounded-t-lg p-4 w-32 text-center">
                      <p className="text-white font-bold text-sm truncate">
                        {topContributors[2]?.email.split('@')[0]}
                      </p>
                      <p className="text-white text-2xl font-bold">{topContributors[2]?.score}</p>
                      <p className="text-white text-xs">points</p>
                    </div>
                    <div className="bg-amber-700 h-16 w-32 flex items-center justify-center text-white font-bold text-2xl">
                      3
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Rest of the list */}
              <div className="space-y-2">
                {topContributors.slice(3).map((user, index) => (
                  <motion.div
                    key={user.email}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.05 }}
                  >
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="text-2xl font-bold text-gray-400 w-8">
                        {index + 4}
                      </span>
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-emerald-100 text-emerald-700">
                          {user.email[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {user.email.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user.reports} reports • {user.comments} comments
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                        {user.score} pts
                      </Badge>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </div>

          {/* Side Stats */}
          <div className="space-y-6">
            {/* Top Neighborhoods */}
            <Card className="p-6 bg-white/90 backdrop-blur-sm">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Most Active Areas
              </h3>
              <div className="space-y-3">
                {topNeighborhoods.map((neighborhood, index) => (
                  <div key={neighborhood.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{index + 1}.</span>
                      <span className="text-sm text-gray-900">{neighborhood.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {neighborhood.total}
                      </Badge>
                      <Star className={`w-4 h-4 ${index < 3 ? 'text-yellow-500' : 'text-gray-300'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Top Categories */}
            <Card className="p-6 bg-white/90 backdrop-blur-sm">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Top Issue Categories
              </h3>
              <div className="space-y-3">
                {topCategories.map((item, index) => (
                  <div key={item.category} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">
                      {item.category.replace('_', ' ')}
                    </span>
                    <Badge className="bg-purple-100 text-purple-700">
                      {item.count}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Community Stats */}
            <Card className="p-6 bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Community Stats
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Total Contributors</span>
                  <span className="text-lg font-bold text-emerald-700">
                    {Object.keys(userStats).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Total Reports</span>
                  <span className="text-lg font-bold text-blue-700">
                    {reports.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Issues Resolved</span>
                  <span className="text-lg font-bold text-green-700">
                    {reports.filter(r => r.status === 'resolved').length}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}